import { useCallback } from 'react';
import { Layer, MapGeoJSONFeature } from 'shared/map-exports';
import { useMapLayersState } from 'state/useMapLayersState';
import { usePoisState } from 'state/usePoisState';
import { useAppInfo } from 'hooks/useAppInfo';
import {
  BELOW_STREETS_LAYER_ID,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles/constants';
import { useMapLayerEvents } from 'features/mapping/hooks/useMapLayerEvents';
import { getWatchdutyTileUrl } from 'features/mapping/components/layers/layers.utils';
import { VisibilityToggledSource } from '../VisibilityToggledSource';
import {
  FEATURE_UID_V2_FIELD_NAME,
  INACTIVE_ZONES_LAYER_MIN_ZOOM,
} from './constants';
import {
  switchZoneStatus,
  createGroupings,
  createColorGroupings,
  getFillLayerId,
  createPatternGroupings,
} from './utils';
import {
  getEditFillStyle,
  getEditLabelStyle,
  getEditStrokeStyle,
  getUidFillStyle,
  getEditUidPatternStyle,
} from './layerStyles';
import { StructuredEvacuationsEditLayerProps } from './types';
import { EvacZoneStyles } from '../../../../../constants';

const REFRESH_INTERVAL_SECONDS = 30;

/*
 * This is a base layer to facilitate the editing of evacuation zone statuses for incidents.
 * It provides interactive capabilities for updating zone statuses while maintaining
 * real-time visualization throughout the editing process.
 */
export const StructuredEvacuationsEditLayer = (
  props: StructuredEvacuationsEditLayerProps,
): JSX.Element => {
  const {
    visible,
    evacZones,
    onClick,
    sourceId,
    layerName,
    zoneSourceLayer,
    labelSourceLayer,
    zoneLayerPrefix,
    zoneLayerFillPrefix = `${zoneLayerPrefix}-fill`,
  } = props;
  const { isDarkBaseLayer } = useMapLayersState();
  const { clearSelectedPois } = usePoisState();
  const { appVersion } = useAppInfo();

  const clickCallback = useCallback(
    (features: MapGeoJSONFeature[]) => {
      // ensure other POIs are not opening up.
      clearSelectedPois();

      const feature = features[0];
      if (!feature || !feature.properties) return;

      const evacZone = evacZones.find((zone) => zone.uidV2 === feature.id);
      if (!evacZone) return;

      const currentStatus = evacZone.status;
      const status = switchZoneStatus(
        evacZone.style || EvacZoneStyles.default,
        currentStatus,
      );

      onClick && onClick(evacZone.uidV2, status);
    },
    [clearSelectedPois, evacZones, onClick],
  );

  useMapLayerEvents({
    layerId: zoneLayerFillPrefix,
    enabled: visible,
    onClick: clickCallback,
  });

  const zonesWithStatus = evacZones.filter((evacZone) =>
    Boolean(evacZone.status),
  );
  const zonesWithOutStatus = evacZones.filter((evacZone) => !evacZone.status);
  const zoneIdsWithOutStatus = zonesWithOutStatus.map(
    (evacZone) => evacZone.uidV2,
  );

  const allZoneIds = evacZones.map((evacZone) => evacZone.uidV2);

  // Accepted rename holds preview their pending name over the tile label.
  const labelNameOverrides = evacZones.reduce<Record<string, string>>(
    (acc, evacZone) => {
      if (evacZone.pendingDisplayName) {
        acc[evacZone.uidV2] = evacZone.pendingDisplayName;
      }
      return acc;
    },
    {},
  );

  const groups = createGroupings(zonesWithStatus);
  const colorGroups = createColorGroupings(groups);

  const colorKeys = Object.keys(colorGroups);
  colorKeys.forEach((color) => {
    const layerId = getFillLayerId(color, zoneLayerPrefix);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useMapLayerEvents({ layerId, enabled: visible, onClick: clickCallback });
  });

  return (
    <VisibilityToggledSource
      url={getWatchdutyTileUrl(layerName, {
        app_version: appVersion,
      })}
      visible={visible}
      promoteId={FEATURE_UID_V2_FIELD_NAME}
      // Do not unset `volatile` ever for this layer.
      volatile
      id={sourceId}
      refreshIntervalSeconds={REFRESH_INTERVAL_SECONDS}
    >
      <Layer
        id={zoneLayerFillPrefix}
        minzoom={INACTIVE_ZONES_LAYER_MIN_ZOOM}
        beforeId={BELOW_STREETS_LAYER_ID}
        {...getEditFillStyle(zoneIdsWithOutStatus, zoneSourceLayer)}
      />
      {Object.entries(colorGroups).map(([color, uids]) => (
        <Layer
          minzoom={INACTIVE_ZONES_LAYER_MIN_ZOOM}
          beforeId={BELOW_STREETS_LAYER_ID}
          key={color}
          id={getFillLayerId(color, zoneLayerPrefix)}
          {...getUidFillStyle({
            color,
            zoneIds: uids,
            sourceLayer: zoneSourceLayer,
          })}
        />
      ))}
      {Object.entries(createPatternGroupings(groups)).map(([pattern, uids]) => (
        <Layer
          minzoom={INACTIVE_ZONES_LAYER_MIN_ZOOM}
          beforeId={BELOW_STREETS_LAYER_ID}
          key={pattern}
          {...getEditUidPatternStyle(
            pattern,
            uids,
            zoneSourceLayer,
            `${zoneLayerPrefix}-pattern`,
          )}
        />
      ))}
      <Layer
        id={`${zoneLayerPrefix}-stroke`}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        minzoom={INACTIVE_ZONES_LAYER_MIN_ZOOM}
        {...getEditStrokeStyle(isDarkBaseLayer, allZoneIds, zoneSourceLayer)}
      />
      <Layer
        id={`${zoneLayerPrefix}-label`}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        minzoom={INACTIVE_ZONES_LAYER_MIN_ZOOM}
        {...getEditLabelStyle(allZoneIds, labelSourceLayer, labelNameOverrides)}
      />
    </VisibilityToggledSource>
  );
};
